import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { adminApi, formatApiError } from './adminApi';
import { ArrowLeft, Save, AlertCircle, CheckCircle2, Plus, Trash2 } from 'lucide-react';
import ImageUploader from './ImageUploader';

const slugify = (s) =>
  (s || '')
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '')
    .trim()
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-');

const EMPTY = {
  id: '',
  title: '',
  category: '',
  client: '',
  industry: '',
  stats: [{ value: '', label: '' }, { value: '', label: '' }],
  heroImage: '',
  dashboardImage: '',
  dashboardImage2: '',
  statHighlight: '',
  statLabel: '',
  description: '',
  whatWasDone: '',
  platform: '',
};

const Field = ({ label, hint, required, children }) => (
  <div className="mb-5">
    <label className="block text-[11px] uppercase tracking-[0.15em] text-white/50 font-['Inter'] mb-2">
      {label}
      {required && <span className="text-[#83BF42] ml-1">*</span>}
    </label>
    {children}
    {hint && <p className="text-white/30 text-xs font-['Lato'] mt-1.5">{hint}</p>}
  </div>
);

const AdminCaseEditor = () => {
  const { id: routeId } = useParams();
  const isNew = !routeId || routeId === 'new';
  const navigate = useNavigate();

  const [form, setForm] = useState(EMPTY);
  const [originalId, setOriginalId] = useState(null);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [touched, setTouched] = useState(false);

  useEffect(() => {
    if (isNew) return;
    setLoading(true);
    adminApi
      .listCaseStudies()
      .then((all) => {
        const found = all.find((c) => c.id === routeId);
        if (!found) {
          setError('Case study not found.');
        } else {
          setForm({
            id: found.id,
            title: found.title || '',
            category: found.category || '',
            client: found.client || '',
            industry: found.industry || '',
            stats: found.stats?.length ? found.stats : EMPTY.stats,
            heroImage: found.heroImage || '',
            dashboardImage: found.dashboardImage || '',
            dashboardImage2: found.dashboardImage2 || '',
            statHighlight: found.statHighlight || '',
            statLabel: found.statLabel || '',
            description: found.description || '',
            whatWasDone: found.whatWasDone || '',
            platform: found.platform || '',
          });
          setOriginalId(found.id);
        }
      })
      .finally(() => setLoading(false));
  }, [isNew, routeId]);

  // Auto-generate id from client when creating
  useEffect(() => {
    if (isNew && !touched && form.client) {
      setForm((f) => ({ ...f, id: `cs-${slugify(f.client)}` }));
    }
  }, [form.client, isNew, touched]);

  const handleChange = (k) => (e) =>
    setForm({ ...form, [k]: e.target.value });

  const handleStatChange = (idx, key, value) => {
    const stats = [...form.stats];
    stats[idx] = { ...stats[idx], [key]: value };
    setForm({ ...form, stats });
  };
  const addStat = () => setForm({ ...form, stats: [...form.stats, { value: '', label: '' }] });
  const removeStat = (idx) => setForm({ ...form, stats: form.stats.filter((_, i) => i !== idx) });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setSaving(true);
    try {
      const payload = {
        ...form,
        stats: form.stats.filter((s) => s.value && s.label),
      };
      if (isNew) {
        const created = await adminApi.createCaseStudy(payload);
        setSuccess('Case study created.');
        setOriginalId(created.id);
        setTimeout(() => navigate(`/admin/cases/${created.id}`, { replace: true }), 800);
      } else {
        const { id, ...rest } = payload;
        await adminApi.updateCaseStudy(originalId, rest);
        setSuccess('Saved.');
      }
    } catch (err) {
      setError(formatApiError(err?.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-20">
        <div className="w-8 h-8 border-2 border-[#83BF42] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="px-8 lg:px-12 py-10 max-w-6xl" data-testid="admin-case-editor">
      <button
        onClick={() => navigate('/admin/cases')}
        data-testid="case-editor-back"
        className="inline-flex items-center gap-2 text-white/50 text-sm font-['Inter'] mb-6 hover:text-white transition-colors"
      >
        <ArrowLeft className="w-4 h-4" /> Back to case studies
      </button>

      <header className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8">
        <div>
          <p className="text-[#83BF42] text-xs uppercase tracking-[0.2em] font-['Inter'] mb-2">
            {isNew ? 'New case study' : 'Edit case study'}
          </p>
          <h1 className="text-white text-3xl font-light font-['Inter']">
            {isNew ? 'Add a client success story' : form.title || form.client || 'Untitled'}
          </h1>
        </div>
        <button
          form="case-form"
          type="submit"
          disabled={saving}
          data-testid="case-editor-save"
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#83BF42] text-[#0F0F11] text-sm font-medium font-['Inter'] hover:bg-[#A8D96C] transition disabled:opacity-50"
        >
          <Save className="w-4 h-4" />
          {saving ? 'Saving…' : isNew ? 'Publish' : 'Save changes'}
        </button>
      </header>

      {error && (
        <div data-testid="case-editor-error" className="flex items-start gap-2 mb-5 px-4 py-3 rounded-lg bg-red-500/10 border border-red-500/20 text-red-300 text-sm">
          <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" /> {error}
        </div>
      )}
      {success && (
        <div data-testid="case-editor-success" className="flex items-start gap-2 mb-5 px-4 py-3 rounded-lg bg-[#83BF42]/10 border border-[#83BF42]/20 text-[#83BF42] text-sm">
          <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0" /> {success}
        </div>
      )}

      <form id="case-form" onSubmit={handleSubmit} className="bg-[#15151A] border border-white/5 rounded-2xl p-6 lg:p-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2">
            <Field label="Title" required>
              <input
                required
                value={form.title}
                onChange={handleChange('title')}
                data-testid="case-title"
                className="w-full px-4 py-3 rounded-lg bg-white/5 border border-white/10 text-white text-lg font-['Inter'] focus:outline-none focus:border-[#83BF42] transition-colors"
                placeholder="Scaling user acquisition for…"
              />
            </Field>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="Client" required>
                <input
                  required
                  value={form.client}
                  onChange={(e) => {
                    setTouched(false);
                    handleChange('client')(e);
                  }}
                  data-testid="case-client"
                  className="w-full px-4 py-3 rounded-lg bg-white/5 border border-white/10 text-white font-['Inter'] focus:outline-none focus:border-[#83BF42] transition-colors"
                />
              </Field>
              <Field label="Industry" required>
                <input
                  required
                  value={form.industry}
                  onChange={handleChange('industry')}
                  data-testid="case-industry"
                  className="w-full px-4 py-3 rounded-lg bg-white/5 border border-white/10 text-white font-['Inter'] focus:outline-none focus:border-[#83BF42] transition-colors"
                  placeholder="Fintech"
                />
              </Field>
            </div>

            <Field label="Description" required hint="Short summary shown on the case-study card.">
              <textarea
                required
                rows={3}
                value={form.description}
                onChange={handleChange('description')}
                data-testid="case-description"
                className="w-full px-4 py-3 rounded-lg bg-white/5 border border-white/10 text-white font-['Lato'] text-sm focus:outline-none focus:border-[#83BF42] transition-colors resize-none"
              />
            </Field>

            <Field label="What was done" hint="Longer narrative shown in the modal.">
              <textarea
                rows={4}
                value={form.whatWasDone}
                onChange={handleChange('whatWasDone')}
                data-testid="case-whatwasdone"
                className="w-full px-4 py-3 rounded-lg bg-white/5 border border-white/10 text-white font-['Lato'] text-sm focus:outline-none focus:border-[#83BF42] transition-colors resize-none"
              />
            </Field>

            {/* Stat highlight */}
            <div className="grid grid-cols-2 gap-4">
              <Field label="Stat highlight" hint="Big number on hero card. e.g. 50,000+">
                <input
                  value={form.statHighlight}
                  onChange={handleChange('statHighlight')}
                  data-testid="case-stathighlight"
                  className="w-full px-4 py-3 rounded-lg bg-white/5 border border-white/10 text-white text-lg font-bold font-['Inter'] focus:outline-none focus:border-[#83BF42] transition-colors"
                />
              </Field>
              <Field label="Stat label">
                <input
                  value={form.statLabel}
                  onChange={handleChange('statLabel')}
                  data-testid="case-statlabel"
                  className="w-full px-4 py-3 rounded-lg bg-white/5 border border-white/10 text-white text-xs font-['Inter'] focus:outline-none focus:border-[#83BF42] transition-colors"
                />
              </Field>
            </div>

            {/* Repeatable stats */}
            <div className="mt-2">
              <div className="flex items-center justify-between mb-2">
                <p className="text-[11px] uppercase tracking-[0.15em] text-white/50 font-['Inter']">
                  Stats grid
                </p>
                <button
                  type="button"
                  onClick={addStat}
                  data-testid="case-add-stat"
                  className="text-[#83BF42] text-xs flex items-center gap-1 hover:underline"
                >
                  <Plus className="w-3 h-3" /> Add stat
                </button>
              </div>
              <div className="space-y-2">
                {form.stats.map((s, i) => (
                  <div key={i} className="flex gap-2" data-testid={`case-stat-row-${i}`}>
                    <input
                      placeholder="Value (e.g. 50K+)"
                      value={s.value}
                      onChange={(e) => handleStatChange(i, 'value', e.target.value)}
                      className="flex-1 px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-white text-sm font-['Inter'] focus:outline-none focus:border-[#83BF42] transition-colors"
                    />
                    <input
                      placeholder="Label"
                      value={s.label}
                      onChange={(e) => handleStatChange(i, 'label', e.target.value)}
                      className="flex-1 px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-white text-sm font-['Lato'] focus:outline-none focus:border-[#83BF42] transition-colors"
                    />
                    <button
                      type="button"
                      onClick={() => removeStat(i)}
                      className="text-red-400/70 hover:text-red-400 p-2"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <aside className="lg:col-span-1 space-y-1">
            <Field label="ID (slug)" required hint="URL: /work#{id}">
              <input
                required
                value={form.id}
                onChange={(e) => {
                  setTouched(true);
                  setForm({ ...form, id: slugify(e.target.value) });
                }}
                disabled={!isNew}
                data-testid="case-id"
                className="w-full px-4 py-3 rounded-lg bg-white/5 border border-white/10 text-white font-mono text-sm focus:outline-none focus:border-[#83BF42] transition-colors disabled:opacity-50"
              />
            </Field>

            <Field label="Category">
              <input
                value={form.category}
                onChange={handleChange('category')}
                data-testid="case-category"
                className="w-full px-4 py-3 rounded-lg bg-white/5 border border-white/10 text-white font-['Inter'] text-sm focus:outline-none focus:border-[#83BF42] transition-colors"
                placeholder="Paid Media"
              />
            </Field>

            <Field label="Platform">
              <input
                value={form.platform}
                onChange={handleChange('platform')}
                data-testid="case-platform"
                className="w-full px-4 py-3 rounded-lg bg-white/5 border border-white/10 text-white font-['Inter'] text-sm focus:outline-none focus:border-[#83BF42] transition-colors"
                placeholder="Meta Ads & Grafana"
              />
            </Field>

            <Field label="Hero image" hint="Main card image (16:9 looks best).">
              <ImageUploader
                value={form.heroImage}
                onChange={(url) => setForm({ ...form, heroImage: url })}
                testId="case-hero-image-uploader"
              />
            </Field>

            <Field label="Dashboard image">
              <ImageUploader
                value={form.dashboardImage}
                onChange={(url) => setForm({ ...form, dashboardImage: url })}
                testId="case-dashboard-image-uploader"
              />
            </Field>

            <Field label="Dashboard image 2">
              <ImageUploader
                value={form.dashboardImage2}
                onChange={(url) => setForm({ ...form, dashboardImage2: url })}
                testId="case-dashboard-image2-uploader"
              />
            </Field>
          </aside>
        </div>
      </form>
    </div>
  );
};

export default AdminCaseEditor;
